import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { PATH } from '@/router/PATH';
import { getNodeTestIdProp } from '@/utils';
import { ProductDetailSkeleton } from '../productDetail/ProductDetailSkeleton';
import { ProductNotFound } from '../productDetail/ProductNotFound';
import { EditProductView } from './EditProductView';
import { useEditProductForm } from './useEditProductForm';

/**
 * /products/:productId/edit — the **fields-only** edit form. Loads the product,
 * seeds an editable draft (`useEditProductForm`), and PUTs metadata + existing
 * variants' fields. By construction it can't change axes, add/remove variants,
 * or touch batches. Delete lives here too (returns to the list). A product
 * that no longer exists gets the details page's not-found state (bos-app#136).
 */
export const EditProduct: React.FC = () => {
    const form = useEditProductForm();
    const { t } = useTranslation('pages');

    if (form.isLoading) {
        return (
            <PageShell>
                <ProductDetailSkeleton
                    data-testid="edit-product-loading"
                    id="edit-product-loading"
                />
            </PageShell>
        );
    }

    if (form.loadStatus === 'notFound') {
        return (
            <PageShell>
                <ProductNotFound />
            </PageShell>
        );
    }

    if (form.loadError) {
        return (
            <PageShell>
                <Widget
                    className="flex flex-col items-start gap-3 p-6"
                    {...getNodeTestIdProp('edit-product-load-error')}
                >
                    <h1 className="text-lg font-semibold text-foreground">
                        {t('products.edit.error.title')}
                    </h1>
                    <p className="text-sm text-muted">
                        {t('products.edit.error.message')}
                    </p>
                    <div className="flex items-center gap-3">
                        <Button
                            onPress={form.onRetry}
                            variant="primary"
                            {...getNodeTestIdProp('edit-product-load-retry')}
                        >
                            {t('products.edit.error.retry')}
                        </Button>
                        <Link
                            className="text-sm text-muted hover:text-foreground"
                            to={PATH.PRODUCTS.ROOT}
                        >
                            {t('products.edit.error.back')}
                        </Link>
                    </div>
                </Widget>
            </PageShell>
        );
    }

    if (!form.state) {
        return (
            <PageShell>
                <ProductDetailSkeleton
                    data-testid="edit-product-loading"
                    id="edit-product-loading"
                />
            </PageShell>
        );
    }

    return (
        <PageShell>
            <EditProductView form={form} state={form.state} />
        </PageShell>
    );
};

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div
        className="mx-auto flex w-full max-w-5xl flex-col gap-8"
        {...getNodeTestIdProp('edit-product-page')}
        id="edit-product-page"
    >
        {children}
    </div>
);
